import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CurrencyDollar, CalendarBlank, FileText, Warning, ArrowRight, FolderOpen } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/api/client/dashboard`, { withCredentials: true });
        setStats(data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00AEEF]" /></div>;

  const kpis = [
    { label: 'Total du', value: `${(stats?.total_du || 0).toLocaleString('fr-FR')} €`, icon: CurrencyDollar, color: '#1A2421' },
    { label: 'Total paye', value: `${(stats?.total_paye || 0).toLocaleString('fr-FR')} €`, icon: CurrencyDollar, color: '#2D5A4C' },
    { label: 'Reste a payer', value: `${(stats?.reste_a_payer || 0).toLocaleString('fr-FR')} €`, icon: CurrencyDollar, color: stats?.reste_a_payer > 0 ? '#EA3C3A' : '#2D5A4C' },
    { label: 'En attente', value: stats?.en_attente || 0, icon: CalendarBlank, color: '#FF8E00' },
    { label: 'En retard', value: stats?.en_retard || 0, icon: Warning, color: '#EA3C3A' },
    { label: 'Documents', value: stats?.nb_documents || 0, icon: FileText, color: '#00AEEF' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Bienvenue sur votre espace</h1>
        <p className="text-sm text-[#5E6D66] mt-1">Consultez vos echeanciers, telechargez vos documents et payez en ligne</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="client-kpis">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Card key={i} className="border-[#E8E6E1]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi.color}12` }}>
                    <Icon size={14} style={{ color: kpi.color }} weight="fill" />
                  </div>
                </div>
                <p className="text-lg font-bold text-[#1A2421]" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-[10px] text-[#5E6D66] mt-0.5">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-[#E8E6E1] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/espace-client/dossiers')} data-testid="client-quick-dossiers">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2D5A4C]/10 flex items-center justify-center">
                <FolderOpen size={20} className="text-[#2D5A4C]" weight="fill" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Mes dossiers</p>
                <p className="text-[11px] text-[#5E6D66]">{stats?.nb_dossiers_actifs || 0} actif(s)</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-[#5E6D66]" />
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/espace-client/echeanciers')} data-testid="client-quick-schedules">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center">
                <CalendarBlank size={20} className="text-[#00AEEF]" weight="fill" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Mes echeanciers</p>
                <p className="text-[11px] text-[#5E6D66]">{stats?.nb_echeanciers || 0} echeancier(s)</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-[#5E6D66]" />
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/espace-client/paiements')} data-testid="client-quick-payments">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2D5A4C]/10 flex items-center justify-center">
                <CurrencyDollar size={20} className="text-[#2D5A4C]" weight="fill" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Mes paiements</p>
                <p className="text-[11px] text-[#5E6D66]">{stats?.en_attente || 0} en attente</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-[#5E6D66]" />
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/espace-client/documents')} data-testid="client-quick-documents">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF8E00]/10 flex items-center justify-center">
                <FileText size={20} className="text-[#FF8E00]" weight="fill" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2421]">Mes documents</p>
                <p className="text-[11px] text-[#5E6D66]">{stats?.nb_documents || 0} document(s)</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-[#5E6D66]" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
